import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const BookingConfirmation = ({ bookingData, hostel }) => {
  const navigate = useNavigate();
  const [showReceipt, setShowReceipt] = useState(false);

  // Generate booking reference
  const bookingReference = `MUH${Date.now().toString().slice(-8)}`;

  const handleDownloadReceipt = () => {
    // In a real app, this would generate and download a PDF receipt
    const receiptData = {
      bookingReference,
      hostel: hostel.name,
      university: hostel.university,
      studentInfo: bookingData.studentInfo,
      roomSelection: bookingData.roomSelection,
      bookingDetails: bookingData.bookingDetails,
      paymentInfo: bookingData.paymentInfo,
      totalAmount: calculateTotal()
    };

    // Mock download - in real app, send to server for PDF generation
    const dataStr = JSON.stringify(receiptData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);

    const exportFileDefaultName = `booking-receipt-${bookingReference}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const calculateTotal = () => {
    const roomType = bookingData.roomSelection?.roomType;
    if (!roomType) return 0;

    let multiplier = 1;
    switch (bookingData.bookingDetails?.duration) {
      case 'semester':
        multiplier = 4.5;
        break;
      case 'academic_year':
        multiplier = 10;
        break;
    }

    const subtotal = roomType.price * multiplier;
    const serviceFee = subtotal * 0.05;
    return Math.round(subtotal + serviceFee);
  };

  const getDurationText = (duration) => {
    switch (duration) {
      case 'semester':
        return 'One Semester (4-5 months)';
      case 'academic_year':
        return 'Academic Year (9-10 months)';
      default:
        return 'Custom Duration';
    }
  };

  if (showReceipt) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Booking Receipt</h2>
          <Button variant="outline" onClick={() => setShowReceipt(false)}>
            <Icon name="X" size={16} className="mr-2" />
            Close
          </Button>
        </div>

        {/* Receipt Header */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 text-center">
          <Icon name="CheckCircle" size={48} className="text-primary mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-primary mb-2">Booking Confirmed!</h3>
          <p className="text-muted-foreground">Your hostel accommodation has been successfully booked</p>
          <div className="mt-4 p-3 bg-primary/10 rounded-lg">
            <p className="text-sm font-medium">Booking Reference</p>
            <p className="text-lg font-bold text-primary">{bookingReference}</p>
          </div>
        </div>

        {/* Receipt Details */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Student Information */}
            <div>
              <h4 className="font-semibold text-foreground mb-3">Student Information</h4>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium">Name:</span> {bookingData.studentInfo?.fullName}</div>
                <div><span className="font-medium">Student ID:</span> {bookingData.studentInfo?.studentId}</div>
                <div><span className="font-medium">Email:</span> {bookingData.studentInfo?.universityEmail}</div>
                <div><span className="font-medium">Faculty:</span> {bookingData.studentInfo?.faculty}</div>
                <div><span className="font-medium">Year:</span> {bookingData.studentInfo?.yearOfStudy}</div>
              </div>
            </div>

            {/* Booking Details */}
            <div>
              <h4 className="font-semibold text-foreground mb-3">Booking Details</h4>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium">Hostel:</span> {hostel.name}</div>
                <div><span className="font-medium">University:</span> {hostel.university}</div>
                <div><span className="font-medium">Room Type:</span> {bookingData.roomSelection?.roomType?.type}</div>
                <div><span className="font-medium">Duration:</span> {getDurationText(bookingData.bookingDetails?.duration)}</div>
                <div><span className="font-medium">Check-in Date:</span> {new Date(bookingData.bookingDetails?.checkInDate).toLocaleDateString()}</div>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="mt-6 pt-6 border-t border-border">
            <h4 className="font-semibold text-foreground mb-3">Payment Information</h4>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Payment Method:</span>
                <span className="capitalize">{bookingData.paymentInfo?.method || 'Card'}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Transaction ID:</span>
                <span className="font-mono text-sm">{bookingData.paymentInfo?.transactionId}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Payment Date:</span>
                <span>{new Date(bookingData.paymentInfo?.timestamp || Date.now()).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-border">
                <span className="font-bold text-lg">Total Paid:</span>
                <span className="font-bold text-lg text-primary">UGX {calculateTotal().toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Important Information */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <Icon name="Info" size={20} className="text-blue-500 mr-3 mt-0.5" />
              <div>
                <h5 className="text-blue-800 font-medium">Next Steps</h5>
                <ul className="text-blue-700 text-sm mt-2 space-y-1">
                  <li>• Check your email for booking confirmation and hostel rules</li>
                  <li>• Report to hostel administration office with this receipt</li>
                  <li>• Complete room assignment and key collection on check-in date</li>
                  <li>• Bring valid student ID and admission letter for verification</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={handleDownloadReceipt}>
            <Icon name="Download" size={16} className="mr-2" />
            Download Receipt
          </Button>
          <Button variant="outline" onClick={() => navigate('/hostels')}>
            <Icon name="ArrowLeft" size={16} className="mr-2" />
            Back to Hostels
          </Button>
          <Button variant="outline" onClick={() => navigate('/user-dashboard')}>
            <Icon name="User" size={16} className="mr-2" />
            My Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Booking Confirmation</h2>
        <p className="text-muted-foreground">
          Please review your booking details and confirm your reservation.
        </p>
      </div>

      {/* Success Message */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <Icon name="CheckCircle" size={48} className="text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-green-800 mb-2">Payment Successful!</h3>
        <p className="text-green-700">
          Your hostel booking has been processed successfully. Your booking reference is:
        </p>
        <div className="mt-4 p-3 bg-green-100 rounded-lg inline-block">
          <p className="text-lg font-bold text-green-800">{bookingReference}</p>
        </div>
      </div>

      {/* Booking Summary */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Booking Summary</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Hostel & Room Details */}
          <div className="space-y-3">
            <div>
              <span className="font-medium text-muted-foreground">Hostel:</span>
              <p className="font-medium">{hostel.name}</p>
              <p className="text-sm text-muted-foreground">{hostel.university}</p>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">Room Type:</span>
              <p>{bookingData.roomSelection?.roomType?.type}</p>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">Duration:</span>
              <p>{getDurationText(bookingData.bookingDetails?.duration)}</p>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">Check-in Date:</span>
              <p>{new Date(bookingData.bookingDetails?.checkInDate).toLocaleDateString()}</p>
            </div>
          </div>

          {/* Student & Payment Details */}
          <div className="space-y-3">
            <div>
              <span className="font-medium text-muted-foreground">Student:</span>
              <p>{bookingData.studentInfo?.fullName}</p>
              <p className="text-sm text-muted-foreground">{bookingData.studentInfo?.studentId}</p>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">Payment Method:</span>
              <p className="capitalize">{bookingData.paymentInfo?.method || 'Card'}</p>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">Transaction ID:</span>
              <p className="font-mono text-sm">{bookingData.paymentInfo?.transactionId}</p>
            </div>
            <div>
              <span className="font-medium text-muted-foreground">Total Amount:</span>
              <p className="text-lg font-bold text-primary">UGX {calculateTotal().toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notes */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <Icon name="AlertTriangle" size={20} className="text-yellow-500 mr-3 mt-0.5" />
          <div>
            <h4 className="text-yellow-800 font-medium">Important Information</h4>
            <ul className="text-yellow-700 text-sm mt-2 space-y-1">
              <li>• A confirmation email has been sent to your university email address</li>
              <li>• Report to the hostel administration office with this booking reference</li>
              <li>• Bring your student ID and admission letter for room assignment</li>
              <li>• Check-in time starts at 2:00 PM on your check-in date</li>
              <li>• Late check-ins must be arranged with hostel management</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Icon name="Phone" size={20} className="text-blue-500 mr-3 mt-0.5" />
          <div>
            <h4 className="text-blue-800 font-medium">Need Help?</h4>
            <div className="text-blue-700 text-sm mt-2">
              <p><strong>Hostel Administration:</strong> {hostel.contact?.phone}</p>
              <p><strong>Email:</strong> {hostel.contact?.email}</p>
              <p><strong>Office:</strong> {hostel.contact?.office}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-6">
        <Button onClick={() => setShowReceipt(true)} className="min-w-[200px]">
          <Icon name="FileText" size={16} className="mr-2" />
          View Receipt
        </Button>
        <Button variant="outline" onClick={() => navigate('/hostels')}>
          <Icon name="ArrowLeft" size={16} className="mr-2" />
          Back to Hostels
        </Button>
        <Button variant="outline" onClick={() => navigate('/user-dashboard')}>
          <Icon name="User" size={16} className="mr-2" />
          My Dashboard
        </Button>
      </div>
    </div>
  );
};

export default BookingConfirmation;